import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import type { Property } from 'shared-types/Property';
import type { JourneyPickerCategory } from 'voyager';
import { useHomeCategories } from '@features/Home/v2/useHomeCategories';
import { useLabels } from '@features/Menu/useLabels';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { settingSelectors } from '@store/setting/selectors';

export function useNavCategories(): JourneyPickerCategory[] {
  const property = useSelector(entitySelectors.getProperty) as Property;
  const language = (useSelector(settingSelectors.getUserLanguage) as string | undefined) ?? 'en';
  const labels = useLabels();
  const { t } = useTranslations();
  const isCollectionsVisible = useFeatureIsOn('feature.ecom2');

  const { categories } = useHomeCategories({ property, language });

  return useMemo(() => {
    const mapped: JourneyPickerCategory[] = categories.map(({ id, label, imageUrl }) => ({
      id,
      label,
      imageUrl: imageUrl ?? undefined,
      imageAlt: label,
    }));

    if (isCollectionsVisible) {
      const collectionsLabel = labels?.collections || t('flynav.collections');
      mapped.push({
        id: 'collections',
        label: collectionsLabel,
        imageAlt: collectionsLabel,
      });
    }

    return mapped;
  }, [categories, isCollectionsVisible, labels, t]);
}
